/**
 * API Client for Virtual Tour Easy Dashboard
 * Handles all HTTP requests with automatic token refresh
 */

import { isServiceUnavailableError, showServiceUnavailable } from './serviceAvailability'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787'

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 30000

class ApiError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean
  timeout?: number
}

function parseMaybeJson(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function detailMessage(value: unknown): string {
  if (!value) return ''
  if (typeof value === 'string') return value.trim()
  if (Array.isArray(value)) {
    return value
      .map(item => detailMessage(item))
      .filter(Boolean)
      .join(' ')
      .trim()
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    for (const key of ['msg', 'message', 'error', 'detail']) {
      const message = detailMessage(record[key])
      if (message) return message
    }
  }
  return ''
}

function apiErrorMessage(data: unknown, fallback: string): string {
  const body = typeof data === 'string' ? parseMaybeJson(data) : data
  if (typeof body === 'string') return body.trim() || fallback
  if (!body || typeof body !== 'object') return fallback

  const record = body as Record<string, unknown>
  for (const key of ['error', 'message']) {
    const message = detailMessage(record[key])
    if (message) return message
  }

  return detailMessage(record.detail) || fallback
}

// Token refresh state to prevent race conditions
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

async function request<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>)
  }

  // Add auth token if available
  if (!skipAuth) {
    const token = localStorage.getItem('vte_access_token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include'
    })

    // Handle 401 - try to refresh token
    if (response.status === 401 && !skipAuth) {
      const refreshed = await tryRefreshToken()
      if (refreshed) {
        // Retry the request with new token
        const newToken = localStorage.getItem('vte_access_token')
        headers['Authorization'] = `Bearer ${newToken}`
        const retryResponse = await fetch(url, {
          ...fetchOptions,
          headers,
          credentials: 'include'
        })
        return handleResponse<T>(retryResponse)
      }
      // Refresh failed, redirect to login
      localStorage.removeItem('vte_access_token')
      localStorage.removeItem('vte_refresh_token')
      window.location.href = '/login'
      throw new ApiError('Session expired', 401)
    }

    return handleResponse<T>(response)
  } catch (error) {
    if (error instanceof ApiError) throw error
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    )
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type')
  const isJson = contentType?.includes('application/json')

  if (!response.ok) {
    const data = isJson ? await response.json() : await response.text()
    showServiceUnavailable(data)
    throw new ApiError(
      apiErrorMessage(data, `HTTP error ${response.status}`),
      response.status,
      data
    )
  }

  if (isJson) {
    return response.json()
  }

  return response.text() as unknown as T
}

/**
 * True when the API's httpOnly refresh cookie is present (detected via the
 * non-httpOnly `*_logged_in` companion cookie). When it is, we refresh via
 * cookie and never need the refresh token in localStorage — which keeps it
 * out of reach of XSS.
 */
export function hasSessionCookie(): boolean {
  // Cookie names are env-prefixed (dev_vte_logged_in vs vte_logged_in) because
  // dev and prod share the .virtualtoureasy.com cookie domain.
  const prefix = import.meta.env.VITE_ENVIRONMENT === 'production' ? '' : 'dev_'
  return document.cookie.split(';').some(c => c.trim().startsWith(`${prefix}vte_logged_in=`))
}

/** Persist tokens after login/refresh. The refresh token only goes to
 * localStorage as a fallback for browsers/origins where the cross-subdomain
 * cookie didn't stick (e.g. localhost dev, cookie-blocking browsers). */
export function storeTokens(accessToken: string, refreshToken?: string | null): void {
  localStorage.setItem('vte_access_token', accessToken)
  if (refreshToken && !hasSessionCookie()) {
    localStorage.setItem('vte_refresh_token', refreshToken)
  } else {
    localStorage.removeItem('vte_refresh_token')
  }
}

async function tryRefreshToken(): Promise<boolean> {
  // If already refreshing, wait for that to complete (prevents race condition)
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  // Prefer cookie-based refresh; fall back to a locally stored refresh token.
  const refreshToken = localStorage.getItem('vte_refresh_token')
  if (!refreshToken && !hasSessionCookie()) return false

  isRefreshing = true

  refreshPromise = (async () => {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Empty body → the API falls back to the httpOnly refresh cookie
        body: JSON.stringify(refreshToken ? { refreshToken } : {}),
        signal: controller.signal,
        credentials: 'include'
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 403) {
          const data: unknown = await response.clone().json().catch(() => null)
          if (isServiceUnavailableError(data)) {
            showServiceUnavailable(data)
            throw new ApiError(apiErrorMessage(data, 'Service unavailable'), 403, data)
          }
        }
        // Clear tokens on refresh failure
        localStorage.removeItem('vte_access_token')
        localStorage.removeItem('vte_refresh_token')
        return false
      }

      const data = await response.json()
      storeTokens(data.accessToken, data.refreshToken)
      return true
    } catch (error) {
      if (error instanceof ApiError && isServiceUnavailableError(error.data)) throw error
      return false
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// API methods
const api = {
  get: <T = any>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, body?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined
    }),

  put: <T = any>(endpoint: string, body?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined
    }),

  patch: <T = any>(endpoint: string, body?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined
    }),

  delete: <T = any>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),

  /** Fetch AI credit balance, cost reference, plan limits, and recent usage */
  getAiUsage: <T = any>() =>
    request<T>('/api/ai/credits', { method: 'GET' }),

  /** Request a short-lived preview token for an unpublished tour (owner only, 1-hour TTL) */
  requestPreviewToken: (tourUuid: string) =>
    request<{ preview_token: string }>(`/api/tours/${tourUuid}/preview-token`, { method: 'POST' })
}

export default api
export { ApiError }
